/**
 * Marketing Suite API — specialists, runs, artifacts, dashboards, ask,
 * alerts. All calls funnel through the shared apiClient headers.
 */

import { createHeaders } from './apiClient';
import { handleResponse } from './httpResponse';
import type {
  AlertRule,
  AlertRuleInput,
  Artifact,
  ArtifactAsset,
  ArtifactSummary,
  AskResponse,
  Dashboard,
  DashboardData,
  DashboardWidget,
  MetricInfo,
  MetricQueryInput,
  SpecialistListResponse,
  SpecialistRun,
  SpecialistSettingsResult,
  SpecialistSettingsUpdate,
} from '../types/marketingSuite';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

// --- Specialists ---

export async function listSpecialists(): Promise<SpecialistListResponse> {
  const response = await fetch(`${API_BASE_URL}/api/marketing-suite/specialists`, {
    headers: createHeaders(),
  });
  return handleResponse<SpecialistListResponse>(response);
}

export async function setSpecialistEnabled(
  key: string,
  enabled: boolean
): Promise<{ status: string; enabled: boolean }> {
  const response = await fetch(
    `${API_BASE_URL}/api/marketing-suite/specialists/${key}/enable`,
    {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ enabled }),
    }
  );
  return handleResponse(response);
}

/**
 * The specialist's own setup write path (spec #47 §3a) — `cadence_override`
 * plus the `SpecialistConfig` fields `derive_configs` writes.
 *
 * This endpoint has existed with NO caller: until now the only way to
 * change an ICP after onboarding was to re-run the whole interview. The
 * tab adopts it unchanged rather than routing specialist config through
 * the agent settings service, which would be exactly the parallel store
 * §12 forbids (P10). Floors stay server-side: an unknown cadence, an
 * unknown config key or an out-of-range number is a 422, rendered at the
 * field.
 */
export async function updateSpecialistSettings(
  key: string,
  update: SpecialistSettingsUpdate
): Promise<SpecialistSettingsResult> {
  const response = await fetch(
    `${API_BASE_URL}/api/marketing-suite/specialists/${key}/settings`,
    {
      method: 'PATCH',
      headers: createHeaders(),
      body: JSON.stringify(update),
    }
  );
  return handleResponse<SpecialistSettingsResult>(response);
}

export async function runSpecialist(key: string): Promise<SpecialistRun> {
  const response = await fetch(
    `${API_BASE_URL}/api/marketing-suite/specialists/${key}/run`,
    { method: 'POST', headers: createHeaders() }
  );
  return handleResponse<SpecialistRun>(response);
}

export async function listSpecialistRuns(
  key: string,
  limit = 20
): Promise<SpecialistRun[]> {
  const response = await fetch(
    `${API_BASE_URL}/api/marketing-suite/specialists/${key}/runs?limit=${limit}`,
    { headers: createHeaders() }
  );
  return handleResponse<SpecialistRun[]>(response);
}

export async function listArtifacts(
  specialistKey?: string,
  limit = 30
): Promise<ArtifactSummary[]> {
  const params = new URLSearchParams({ limit: String(limit) });
  if (specialistKey) params.append('specialist_key', specialistKey);
  const response = await fetch(
    `${API_BASE_URL}/api/marketing-suite/artifacts?${params}`,
    { headers: createHeaders() }
  );
  return handleResponse<ArtifactSummary[]>(response);
}

export async function getArtifact(artifactId: string): Promise<Artifact> {
  const response = await fetch(
    `${API_BASE_URL}/api/marketing-suite/artifacts/${artifactId}`,
    { headers: createHeaders() }
  );
  return handleResponse<Artifact>(response);
}

// T17: presigned GETs for an artifact's generated media (review/download).
export async function getArtifactAssets(
  artifactId: string
): Promise<ArtifactAsset[]> {
  const response = await fetch(
    `${API_BASE_URL}/api/marketing-suite/artifacts/${artifactId}/assets`,
    { headers: createHeaders() }
  );
  return handleResponse<ArtifactAsset[]>(response);
}

// T17: the review UI's approve action — an artifact-level flag. Returns
// the updated ARTIFACT (router convention: return the mutated resource),
// not a bespoke envelope. 404s if the artifact never ran the media step.
export async function approveArtifact(artifactId: string): Promise<Artifact> {
  const response = await fetch(
    `${API_BASE_URL}/api/marketing-suite/artifacts/${artifactId}/approve`,
    { method: 'POST', headers: createHeaders() }
  );
  return handleResponse<Artifact>(response);
}

// --- Dashboards ---

export async function listDashboards(): Promise<Dashboard[]> {
  const response = await fetch(`${API_BASE_URL}/api/dashboards`, {
    headers: createHeaders(),
  });
  return handleResponse<Dashboard[]>(response);
}

export async function createDashboard(
  name: string,
  description?: string
): Promise<Dashboard> {
  const response = await fetch(`${API_BASE_URL}/api/dashboards`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify({ name, description }),
  });
  return handleResponse<Dashboard>(response);
}

export async function deleteDashboard(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/dashboards/${id}`, {
    method: 'DELETE',
    headers: createHeaders(),
  });
  await handleResponse(response);
}

export async function getDashboardData(id: string): Promise<DashboardData> {
  const response = await fetch(`${API_BASE_URL}/api/dashboards/${id}/data`, {
    headers: createHeaders(),
  });
  return handleResponse<DashboardData>(response);
}

export async function addWidget(
  dashboardId: string,
  widget: {
    widget_type: string;
    title: string;
    metric_query: MetricQueryInput;
    position?: number;
  }
): Promise<DashboardWidget> {
  const response = await fetch(
    `${API_BASE_URL}/api/dashboards/${dashboardId}/widgets`,
    {
      method: 'POST',
      headers: createHeaders(),
      body: JSON.stringify({ position: 0, ...widget }),
    }
  );
  return handleResponse<DashboardWidget>(response);
}

export async function deleteWidget(
  dashboardId: string,
  widgetId: string
): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/dashboards/${dashboardId}/widgets/${widgetId}`,
    { method: 'DELETE', headers: createHeaders() }
  );
  await handleResponse(response);
}

export async function createShareToken(
  dashboardId: string
): Promise<{ share_token: string }> {
  const response = await fetch(
    `${API_BASE_URL}/api/dashboards/${dashboardId}/share`,
    { method: 'POST', headers: createHeaders() }
  );
  return handleResponse(response);
}

export async function revokeShareToken(dashboardId: string): Promise<void> {
  const response = await fetch(
    `${API_BASE_URL}/api/dashboards/${dashboardId}/share`,
    { method: 'DELETE', headers: createHeaders() }
  );
  await handleResponse(response);
}

// --- Ask ---

export async function listAskMetrics(): Promise<{ metrics: MetricInfo[] }> {
  const response = await fetch(`${API_BASE_URL}/api/ask/metrics`, {
    headers: createHeaders(),
  });
  return handleResponse(response);
}

export async function ask(question: string): Promise<AskResponse> {
  const response = await fetch(`${API_BASE_URL}/api/ask`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify({ question }),
  });
  return handleResponse<AskResponse>(response);
}

export async function askStructured(
  metricQuery: MetricQueryInput
): Promise<AskResponse> {
  const response = await fetch(`${API_BASE_URL}/api/ask/query`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify({ metric_query: metricQuery }),
  });
  return handleResponse<AskResponse>(response);
}

// --- Alerts ---

export async function listAlertRules(): Promise<AlertRule[]> {
  const response = await fetch(`${API_BASE_URL}/api/alerts`, {
    headers: createHeaders(),
  });
  return handleResponse<AlertRule[]>(response);
}

export async function createAlertRule(
  rule: AlertRuleInput
): Promise<AlertRule> {
  const response = await fetch(`${API_BASE_URL}/api/alerts`, {
    method: 'POST',
    headers: createHeaders(),
    body: JSON.stringify(rule),
  });
  return handleResponse<AlertRule>(response);
}

export async function updateAlertRule(
  id: string,
  rule: AlertRuleInput
): Promise<AlertRule> {
  const response = await fetch(`${API_BASE_URL}/api/alerts/${id}`, {
    method: 'PATCH',
    headers: createHeaders(),
    body: JSON.stringify(rule),
  });
  return handleResponse<AlertRule>(response);
}

export async function deleteAlertRule(id: string): Promise<void> {
  const response = await fetch(`${API_BASE_URL}/api/alerts/${id}`, {
    method: 'DELETE',
    headers: createHeaders(),
  });
  await handleResponse(response);
}

export async function testAlertRule(
  id: string
): Promise<{
  evaluated: boolean;
  value?: number;
  currency?: string | null;
  breached?: boolean;
  reason?: string;
  // Sweep-skip signals: the hourly worker refuses to evaluate rules
  // with a stale upstream source or a missing newest-closed bucket —
  // the Test control must not claim WOULD FIRE in those states
  // (PR #11 review).
  stale_source?: { source: string; status: string } | null;
  latest_period_current?: boolean;
}> {
  const response = await fetch(`${API_BASE_URL}/api/alerts/${id}/test`, {
    method: 'POST',
    headers: createHeaders(),
  });
  return handleResponse(response);
}
